import React, { FC, ReactNode, PropsWithChildren, forwardRef, Ref } from 'react';
import cx from 'classnames';
import { Box } from 'new/elements/Box';
import { Tooltip } from '@mui/material';
import Typography from 'new/elements/TypographySupport';

import styles from './DataCard.module.scss';

type Props = {
  disabled?: boolean;
  onClick?: (event: any) => void;
  rowsSlots: ReactNode[];
  containerClassName?: string;
  className?: string;
  style?: any;
  ref?: Ref<HTMLDivElement>;
} & JSX.IntrinsicAttributes;

type CellProps = PropsWithChildren<{
  className?: string;
  rightCTAsCell?: boolean;
  multiCol?: boolean;
  contextualInfoCell?: boolean;
  nameDescCell?: boolean;
}>;

const DataCardCell: FC<CellProps> = ({
  className,
  children,
  contextualInfoCell = false,
  rightCTAsCell = false,
  multiCol = false,
  nameDescCell = false,
  ...otherProps
}) => {
  return (
    <div
      {...otherProps}
      className={cx(
        styles.cell,
        rightCTAsCell && styles.rightCTAsCell,
        multiCol && styles.multiCol,
        nameDescCell && styles.nameDescCell,
        contextualInfoCell && styles.contextualInfoCell,
        className,
      )}
    >
      {children}
    </div>
  );
};

type NameDescProps = {
  name: string;
  description?: string;
  tip?: string | ReactNode;
  className?: string;
};

const NameDescCell: FC<NameDescProps> = ({ tip, name, description, className }) => {
  return (
    <DataCardCell className={cx(styles.nameDescCell, className)}>
      <Tooltip title={name} arrow placement="top-start">
        <Typography variant="body2" className={`ellipsis ${styles.max_container_text}`}>
          {name}
        </Typography>
      </Tooltip>
      {description && (
        <Tooltip title={description} arrow placement="top-start">
          <Typography variant="body3" className={`ellipsis ${styles.max_container_text}`}>
            {description}
          </Typography>
        </Tooltip>
      )}
      <span className={`ellipsis ${styles.max_container_text}`}>{tip}</span>
    </DataCardCell>
  );
};

const DataCard = forwardRef<HTMLDivElement, Props>(
  ({ disabled, rowsSlots, containerClassName, className, style = {}, ...otherProps }, ref) => {
    return (
      <div
        ref={ref}
        style={style}
        {...otherProps}
        className={cx(styles.dataCardWrapper, containerClassName)}
      >
        <Box
          className={cx(
            styles.dataCard,
            disabled !== true && styles.dataCardNotDisabled,
            className,
          )}
        >
          {rowsSlots.map((slot, id) => (
            <div className={styles.row} key={id}>
              {slot}
            </div>
          ))}
        </Box>
      </div>
    );
  },
);

export { DataCardCell, NameDescCell };

DataCard.displayName = 'DataCard';

export default DataCard;
